import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { icons } from './adminSidebarIcons';

// Icono distintivo del agente (robot) para las propuestas automáticas.
const AgenteIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="4" y="8" width="16" height="12" rx="2" />
    <path d="M12 8V4" />
    <circle cx="12" cy="3" r="1" />
    <path d="M2 14h2M20 14h2" />
    <circle cx="9" cy="14" r="1.2" />
    <circle cx="15" cy="14" r="1.2" />
    <path d="M9 17.5h6" />
  </svg>
);

const SEV_BADGE = {
  critico: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
  aviso: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
};

const RevisionPreguntas = ({
  preguntas,
  preguntasSeleccionadas,
  setPreguntasSeleccionadas,
  aprobarPregunta,
  rechazarPregunta,
  handleModificarPregunta,
  aprobarTodasPreguntas,
  rechazarTodasPreguntas,
  propuestasAgente = [],
  onAbrirPropuesta
}) => {
  const [preguntasEliminadas, setPreguntasEliminadas] = useState([]);
  const [modoCompactoForzado, setModoCompactoForzado] = useState(false);
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: 'todas',
    dificultad: 'todas',
    autor: 'todos',
    imagen: 'todas',
    incidencias: false,
    orden: 'recientes'
  });
  const [mostrarControles, setMostrarControles] = useState(false);
  const listaPreguntasRef = useRef(null);

  useEffect(() => {
    const evaluarOverflow = () => {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const container = listaPreguntasRef.current;

      if (!container) {
        setModoCompactoForzado(viewportWidth <= 375);
        return;
      }

      const containerOverflow = container.scrollWidth > container.clientWidth + 1;
      const cards = Array.from(container.querySelectorAll('[data-review-card="true"]'));
      const cardOverflow = cards.some((card) => card.scrollWidth > card.clientWidth + 1);

      setModoCompactoForzado(viewportWidth <= 375 || containerOverflow || cardOverflow);
    };

    evaluarOverflow();
    window.addEventListener('resize', evaluarOverflow);

    const observer = new ResizeObserver(() => evaluarOverflow());
    if (listaPreguntasRef.current) {
      observer.observe(listaPreguntasRef.current);
    }

    return () => {
      window.removeEventListener('resize', evaluarOverflow);
      observer.disconnect();
    };
  }, [
    preguntas.length,
    preguntasSeleccionadas.length,
    filtros.busqueda,
    filtros.categoria,
    filtros.dificultad,
    filtros.autor,
    filtros.imagen,
    filtros.incidencias,
    filtros.orden
  ]);

  const preguntaTieneIncidencias = (pregunta) => {
    const respuestas = [
      pregunta.respuesta_correcta,
      pregunta.respuesta_falsa1,
      pregunta.respuesta_falsa2,
      pregunta.respuesta_falsa3
    ]
      .map((item) => (item || '').trim())
      .filter(Boolean);

    const respuestasUnicas = new Set(respuestas.map((item) => item.toLowerCase()));
    const faltanIncorrectas = [pregunta.respuesta_falsa1, pregunta.respuesta_falsa2, pregunta.respuesta_falsa3]
      .filter((item) => (item || '').trim()).length < 3;

    return faltanIncorrectas || respuestasUnicas.size !== respuestas.length;
  };

  const preguntasActivas = useMemo(
    () => preguntas.filter((pregunta) => !preguntasEliminadas.includes(pregunta.id)),
    [preguntas, preguntasEliminadas]
  );

  const categoriasDisponibles = useMemo(
    () => [...new Set([
      ...preguntasActivas.map((p) => p.categoria),
      ...propuestasAgente.map((p) => p.categoria)
    ].filter(Boolean))].sort(),
    [preguntasActivas, propuestasAgente]
  );

  const dificultadesDisponibles = useMemo(
    () => [...new Set([
      ...preguntasActivas.map((p) => p.dificultad),
      ...propuestasAgente.map((p) => p.dificultad)
    ].filter(Boolean))].sort(),
    [preguntasActivas, propuestasAgente]
  );

  const preguntasFiltradas = useMemo(() => {
    const termino = filtros.busqueda.trim().toLowerCase();

    const filtradas = preguntasActivas.filter((pregunta) => {
      const autor = (pregunta.autor || '').trim();
      const autorNormalizado = autor.toLowerCase();
      const esComunidad = autorNormalizado === 'comunidad';
      const esAnonimo = !autor || autorNormalizado === 'anonimo';
      const tieneImagen = Boolean((pregunta.imagen_url || '').trim());
      const tieneIncidencias = preguntaTieneIncidencias(pregunta);

      const coincideBusqueda =
        !termino ||
        [
          pregunta.pregunta,
          pregunta.respuesta_correcta,
          pregunta.respuesta_falsa1,
          pregunta.respuesta_falsa2,
          pregunta.respuesta_falsa3,
          pregunta.categoria,
          pregunta.etiqueta,
          pregunta.dificultad,
          pregunta.autor
        ]
          .filter(Boolean)
          .some((valor) => String(valor).toLowerCase().includes(termino));

      const coincideCategoria = filtros.categoria === 'todas' || pregunta.categoria === filtros.categoria;
      const coincideDificultad = filtros.dificultad === 'todas' || pregunta.dificultad === filtros.dificultad;

      const coincideAutor =
        filtros.autor === 'todos' ||
        (filtros.autor === 'comunidad' && esComunidad) ||
        (filtros.autor === 'anonimo' && esAnonimo) ||
        (filtros.autor === 'registrado' && !esComunidad && !esAnonimo);

      const coincideImagen =
        filtros.imagen === 'todas' ||
        (filtros.imagen === 'con' && tieneImagen) ||
        (filtros.imagen === 'sin' && !tieneImagen);

      const coincideIncidencias = !filtros.incidencias || tieneIncidencias;

      return (
        coincideBusqueda &&
        coincideCategoria &&
        coincideDificultad &&
        coincideAutor &&
        coincideImagen &&
        coincideIncidencias
      );
    });

    if (filtros.orden === 'dificultad') {
      const pesoDificultad = { facil: 1, medio: 2, dificil: 3 };
      return [...filtradas].sort((a, b) => (pesoDificultad[a.dificultad] || 99) - (pesoDificultad[b.dificultad] || 99));
    }

    if (filtros.orden === 'categoria') {
      return [...filtradas].sort((a, b) => String(a.categoria || '').localeCompare(String(b.categoria || '')));
    }

    return filtradas;
  }, [filtros, preguntasActivas]);

  const idsVisibles = useMemo(() => preguntasFiltradas.map((pregunta) => pregunta.id), [preguntasFiltradas]);

  // Las propuestas del agente reaccionan a los mismos filtros (búsqueda / categoría / dificultad).
  const propuestasAgenteFiltradas = useMemo(() => {
    const termino = filtros.busqueda.trim().toLowerCase();
    return propuestasAgente.filter((prop) => {
      const coincideBusqueda =
        !termino ||
        [prop.pregunta, prop.resumen, prop.respuesta_correcta, prop.categoria, `#${prop.pregunta_id}`, prop.pregunta_id]
          .filter(Boolean)
          .some((valor) => String(valor).toLowerCase().includes(termino));
      const coincideCategoria = filtros.categoria === 'todas' || prop.categoria === filtros.categoria;
      const coincideDificultad = filtros.dificultad === 'todas' || prop.dificultad === filtros.dificultad;
      return coincideBusqueda && coincideCategoria && coincideDificultad;
    });
  }, [propuestasAgente, filtros.busqueda, filtros.categoria, filtros.dificultad]);

  const totalConImagen = useMemo(
    () => preguntasActivas.filter((pregunta) => Boolean((pregunta.imagen_url || '').trim())).length,
    [preguntasActivas]
  );

  const totalConIncidencias = useMemo(
    () => preguntasActivas.filter((pregunta) => preguntaTieneIncidencias(pregunta)).length,
    [preguntasActivas]
  );

  const seleccionadasVisibles = useMemo(
    () => preguntasSeleccionadas.filter((id) => idsVisibles.includes(id)).length,
    [preguntasSeleccionadas, idsVisibles]
  );

  const todasVisiblesSeleccionadas = idsVisibles.length > 0 && seleccionadasVisibles === idsVisibles.length;

  const toggleSeleccionPregunta = (id) => {
    setPreguntasSeleccionadas(prev => {
      if (prev.includes(id)) {
        return prev.filter(preguntaId => preguntaId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const toggleSeleccionarVisibles = () => {
    setPreguntasSeleccionadas((prev) => {
      if (todasVisiblesSeleccionadas) {
        return prev.filter((id) => !idsVisibles.includes(id));
      }

      const conjunto = new Set(prev);
      idsVisibles.forEach((id) => conjunto.add(id));
      return Array.from(conjunto);
    });
  };

  const limpiarFiltros = () => {
    setFiltros({
      busqueda: '',
      categoria: 'todas',
      dificultad: 'todas',
      autor: 'todos',
      imagen: 'todas',
      incidencias: false,
      orden: 'recientes'
    });
  };

  const handleAprobarPregunta = (id) => {
    setPreguntasEliminadas(prev => [...prev, id]);
    setTimeout(() => {
      aprobarPregunta(id);
    }, 300);
  };

  const handleRechazarPregunta = (id) => {
    setPreguntasEliminadas(prev => [...prev, id]);
    setTimeout(() => {
      rechazarPregunta(id);
    }, 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-xl">{icons['revision-preguntas']}</span>
          Preguntas Pendientes
        </h3>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 overflow-hidden">
        <button
          type="button"
          onClick={() => setMostrarControles((prev) => !prev)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/70 dark:hover:bg-slate-700/40 transition-colors"
        >
          <div>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Buscador y filtros</p>
            <p className="text-xs text-slate-500 dark:text-slate-300">Ajusta criterios para moderar por lotes con precision</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100">
            {mostrarControles ? 'Ocultar' : 'Mostrar'}
          </span>
        </button>

        {mostrarControles && (
          <div className="p-4 sm:p-5 border-t border-slate-200 dark:border-slate-700 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-3">
              <div>
                <label className="block text-xs font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-300 mb-1">
                  Buscar pregunta
                </label>
                <input
                  type="text"
                  value={filtros.busqueda}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, busqueda: event.target.value }))}
                  placeholder="Texto, respuesta, etiqueta o autor"
                  className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <select
                  value={filtros.categoria}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, categoria: event.target.value }))}
                  className="px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="todas">Categoria</option>
                  {categoriasDisponibles.map((categoria) => (
                    <option key={categoria} value={categoria}>{categoria}</option>
                  ))}
                </select>

                <select
                  value={filtros.dificultad}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, dificultad: event.target.value }))}
                  className="px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="todas">Dificultad</option>
                  {dificultadesDisponibles.map((dificultad) => (
                    <option key={dificultad} value={dificultad}>{dificultad}</option>
                  ))}
                </select>

                <select
                  value={filtros.autor}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, autor: event.target.value }))}
                  className="px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="todos">Autor</option>
                  <option value="comunidad">Comunidad</option>
                  <option value="anonimo">Anonimo</option>
                  <option value="registrado">Registrado</option>
                </select>

                <select
                  value={filtros.imagen}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, imagen: event.target.value }))}
                  className="px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="todas">Imagen</option>
                  <option value="con">Con imagen</option>
                  <option value="sin">Sin imagen</option>
                </select>

                <select
                  value={filtros.orden}
                  onChange={(event) => setFiltros((prev) => ({ ...prev, orden: event.target.value }))}
                  className="px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                >
                  <option value="recientes">Orden recientes</option>
                  <option value="dificultad">Orden dificultad</option>
                  <option value="categoria">Orden categoria</option>
                </select>

                <label className="inline-flex items-center justify-center gap-2 px-2 py-2.5 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-xs font-medium text-amber-700 dark:text-amber-200">
                  <input
                    type="checkbox"
                    checked={filtros.incidencias}
                    onChange={(event) => setFiltros((prev) => ({ ...prev, incidencias: event.target.checked }))}
                    className="h-4 w-4 accent-amber-500"
                  />
                  Solo incidencias
                </label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={limpiarFiltros}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Limpiar filtros
              </button>

              {preguntasFiltradas.length > 0 && (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={todasVisiblesSeleccionadas}
                    onChange={toggleSeleccionarVisibles}
                    className="h-4 w-4 accent-blue-600"
                  />
                  Seleccionar visibles
                </label>
              )}

              <div className="sm:ml-auto flex flex-wrap gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100">
                  Pendientes: {preguntasActivas.length}
                </span>
                {propuestasAgente.length > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
                    Del agente: {propuestasAgenteFiltradas.length}
                  </span>
                )}
                <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
                  Con imagen: {totalConImagen}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200">
                  Incidencias: {totalConIncidencias}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">
                  Mostrando: {preguntasFiltradas.length}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-300">
              {seleccionadasVisibles} / {idsVisibles.length} visibles seleccionadas
            </p>
          </div>
        )}
      </div>

      {/* Botones para aceptar/eliminar seleccionadas */}
      {preguntasSeleccionadas.length > 0 && (
        <div className="mb-4 flex flex-col lg:flex-row gap-2 lg:gap-4 items-start lg:items-center sticky top-0 z-10 bg-white/95 dark:bg-gray-800/95 backdrop-blur p-3 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={aprobarTodasPreguntas}
            className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors duration-200 text-sm w-full lg:w-auto"
          >
            Aceptar seleccionadas ({preguntasSeleccionadas.length})
          </button>
          <button
            onClick={rechazarTodasPreguntas}
            className="bg-rose-600 text-white px-4 py-2 rounded-md hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700 transition-colors duration-200 text-sm w-full lg:w-auto"
          >
            Eliminar seleccionadas ({preguntasSeleccionadas.length})
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300 mt-1 lg:mt-0 lg:ml-auto">
            {preguntasSeleccionadas.length} pregunta(s) seleccionada(s)
          </span>
        </div>
      )}
      
      {preguntas.length === 0 && propuestasAgente.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300">No hay preguntas pendientes por revisar.</p>
        </div>
      )}

      <div ref={listaPreguntasRef} className="space-y-4 w-full max-w-full overflow-x-hidden">
        {/* Propuestas del agente: aparecen como una pregunta más, con icono
            distintivo. Se muestra primero el PROBLEMA y debajo la pregunta.
            Al pulsarlas se abre el modal de gestión. */}
        {propuestasAgenteFiltradas.map((prop) => (
          <div
            key={`agente-${prop.id}`}
            data-review-card="true"
            onClick={() => onAbrirPropuesta && onAbrirPropuesta(prop)}
            className="w-full max-w-full overflow-x-hidden border rounded-xl p-3 sm:p-4 bg-indigo-50 dark:bg-indigo-950/40 border-indigo-300 dark:border-indigo-700 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white" title="Propuesta del agente">
                <AgenteIcon className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-600 text-white">Agente · En proceso</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase ${SEV_BADGE[prop.severidad] || SEV_BADGE.info}`}>
                    {prop.severidad}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">#{prop.pregunta_id} · {prop.categoria || ''}</span>
                </div>
                {/* Problema primero */}
                <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">{prop.resumen || 'Propuesta de cambio'}</p>
                {/* Pregunta debajo, más pequeña y atenuada */}
                <p className="text-xs text-slate-500 dark:text-slate-300 mt-1 break-words">{prop.pregunta || '(pregunta eliminada)'}</p>
                <p className="text-[11px] text-indigo-600 dark:text-indigo-300 mt-1 font-medium">Pulsa para ver los cambios y gestionarlos</p>
              </div>
            </div>
          </div>
        ))}
        <AnimatePresence>
          {preguntasFiltradas.map(pregunta => {
            const tieneIncidencias = preguntaTieneIncidencias(pregunta);
            const duplicado = pregunta.posible_duplicado || null;
            const duplicadoPorcentaje = duplicado ? Math.round((duplicado.similitud || 0) * 100) : 0;

            return (
              <motion.div
                key={pregunta.id}
                initial={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                data-review-card="true"
                className={`w-full max-w-full overflow-x-hidden border rounded-xl ${modoCompactoForzado ? 'p-2 text-xs' : 'p-3 sm:p-4 max-[375px]:p-2.5 text-sm max-[375px]:text-xs'} bg-white dark:bg-gray-700 dark:text-white transition-colors duration-200 cursor-pointer shadow-sm hover:shadow-md ${
                  preguntasSeleccionadas.includes(pregunta.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
                onClick={() => toggleSeleccionPregunta(pregunta.id)}
              >
                <div className={`flex items-start ${modoCompactoForzado ? 'gap-1.5' : 'gap-3 max-[375px]:gap-2'}`}>
                  <input
                    type="checkbox"
                    checked={preguntasSeleccionadas.includes(pregunta.id)}
                    onChange={() => toggleSeleccionPregunta(pregunta.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 h-4 w-4 accent-blue-600 shrink-0"
                    aria-label={`Seleccionar pregunta ${pregunta.id}`}
                  />
                  <div className={`flex-1 min-w-0 max-w-full ${modoCompactoForzado ? 'space-y-2' : 'space-y-3 max-[375px]:space-y-2'}`}>
                    <div className={`grid ${modoCompactoForzado ? 'grid-cols-[minmax(0,1fr)_minmax(0,0.64fr)] gap-1' : 'grid-cols-[minmax(0,1fr)_minmax(0,0.72fr)] max-[375px]:grid-cols-[minmax(0,1fr)_minmax(0,0.68fr)] gap-2 max-[375px]:gap-1.5'} items-start w-full min-w-0 overflow-hidden`}>
                      <div className="min-w-0 overflow-hidden">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300">Pregunta</p>
                        <p className={`${modoCompactoForzado ? 'text-xs leading-4 max-h-8' : 'text-sm leading-5 max-h-10'} font-semibold text-gray-900 dark:text-white break-words overflow-hidden`}>
                          {pregunta.pregunta || 'Sin pregunta'}
                        </p>
                      </div>
                      <div className="text-right min-w-0 overflow-hidden">
                        <p className="text-[11px] uppercase tracking-wide text-emerald-600 dark:text-emerald-300">Correcta</p>
                        <p className={`${modoCompactoForzado ? 'text-xs leading-4' : 'text-sm'} font-semibold text-emerald-700 dark:text-emerald-200 truncate`}>
                          {pregunta.respuesta_correcta || 'Sin respuesta'}
                        </p>
                      </div>
                    </div>

                    <div className={`flex w-full min-w-0 flex-nowrap ${modoCompactoForzado ? 'gap-0.5 text-[9px]' : 'gap-1 max-[375px]:gap-0.5 text-[10px] sm:text-[11px] max-[375px]:text-[9px]'} overflow-hidden whitespace-nowrap pb-1 sm:pb-0`}>
                      <span className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-100 truncate`}>
                        Cat: {pregunta.categoria || 'Sin'}
                      </span>
                      <span className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 truncate`}>
                        Etiq: {pregunta.etiqueta || 'Sin'}
                      </span>
                      <span className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200 truncate`}>
                        Dif: {pregunta.dificultad || 'Sin'}
                      </span>
                      {pregunta.imagen_url && (
                        <span className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 truncate`}>
                          Con imagen
                        </span>
                      )}
                      {tieneIncidencias && (
                        <span className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200 truncate`}>
                          Revisar
                        </span>
                      )}
                      {duplicado && (
                        <span
                          title={`Similar (${duplicadoPorcentaje}%) a: ${duplicado.pregunta}`}
                          className={`inline-flex min-w-0 flex-1 sm:flex-none sm:max-w-max items-center justify-center rounded-full ${modoCompactoForzado ? 'px-1 py-0.5' : 'px-1.5 sm:px-1.5 max-[375px]:px-1 py-1 sm:py-0.5'} bg-rose-600 text-white truncate`}
                        >
                          Duplicado {duplicadoPorcentaje}%
                        </span>
                      )}
                    </div>

                    {duplicado && (
                      <div className="flex items-start gap-1.5 rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/30 px-2 py-1 text-[11px] text-rose-700 dark:text-rose-200">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0" aria-hidden="true">
                          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span className="min-w-0 break-words">
                          Posible duplicado ({duplicadoPorcentaje}%) de: &ldquo;{duplicado.pregunta}&rdquo;
                        </span>
                      </div>
                    )}

                    <div className={`pt-0.5 flex w-full min-w-0 flex-nowrap ${modoCompactoForzado ? 'gap-1' : 'gap-1.5 max-[375px]:gap-1 sm:gap-2'}`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAprobarPregunta(pregunta.id); }}
                        disabled={tieneIncidencias || Boolean(duplicado)}
                        title={duplicado ? 'No se puede aprobar: es un duplicado de una pregunta existente' : undefined}
                        className={`bg-emerald-600 text-white ${modoCompactoForzado ? 'px-1 py-1.5 text-[10px]' : 'px-1.5 max-[375px]:px-1 sm:px-2 sm:py-1.5 py-2 text-[11px] max-[375px]:text-[10px] sm:text-xs'} rounded-md hover:bg-emerald-700 transition-colors duration-200 leading-tight min-w-0 flex-1 sm:flex-none truncate disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRechazarPregunta(pregunta.id); }}
                        className={`bg-rose-600 text-white ${modoCompactoForzado ? 'px-1 py-1.5 text-[10px]' : 'px-1.5 max-[375px]:px-1 sm:px-2 sm:py-1.5 py-2 text-[11px] max-[375px]:text-[10px] sm:text-xs'} rounded-md hover:bg-rose-700 transition-colors duration-200 leading-tight min-w-0 flex-1 sm:flex-none truncate`}
                      >
                        Rechazar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleModificarPregunta(pregunta); }}
                        className={`bg-orange-500 text-white ${modoCompactoForzado ? 'px-1 py-1.5 text-[10px]' : 'px-1.5 max-[375px]:px-1 sm:px-2 sm:py-1.5 py-2 text-[11px] max-[375px]:text-[10px] sm:text-xs'} rounded-md hover:bg-orange-600 transition-colors duration-200 leading-tight min-w-0 flex-1 sm:flex-none truncate`}
                      >
                        Modificar
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {preguntasActivas.length > 0 && preguntasFiltradas.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300">No hay resultados con los filtros actuales.</p>
        </div>
      )}
    </div>
  );
};

export default RevisionPreguntas;
